'use client';

/**
 * Where the Reels agent's turns and a conversation's video study live.
 *
 * DANI conversations are stored by the DANI API, which never sees a turn the
 * Reels agent answered. How those turns join the DANI history is not decided
 * (dani-plan.md Q13: a DANI append endpoint, or a `pdlc_` table merged on
 * read). Until it is, they stay in this browser tab behind `ReelsTurnStore`,
 * the one seam either answer replaces: swap `reelsTurnStore` for an
 * implementation backed by that endpoint or table and nothing else changes.
 *
 * The record is per conversation. `PENDING_CONVERSATION` holds a study chosen
 * before the conversation exists (the chat opened from the Multimedia tab),
 * claimed by the conversation the first send creates.
 */

import { useSyncExternalStore } from 'react';

import type { VideoStudy } from '@/lib/reel-studio/agent/blocks';

export interface ReelsTurn {
    role: 'user' | 'assistant';
    text: string;
}

export interface ReelsConversation {
    /** The study turns are routed to the Reels agent for, or null when removed. */
    study: VideoStudy | null;
    /** Earlier Reels turns, as text, sent back as the agent's history. */
    turns: ReelsTurn[];
}

export interface ReelsTurnStore {
    get(conversationId: string): ReelsConversation;
    setStudy(conversationId: string, study: VideoStudy | null): void;
    appendTurns(conversationId: string, turns: ReelsTurn[]): void;
    /** Moves a pending record (study chosen before the conversation existed) to `conversationId`. */
    claimPending(conversationId: string): void;
    subscribe(listener: () => void): () => void;
}

export const PENDING_CONVERSATION = '__pending__';

/** Turns kept per conversation; older ones drop off the agent's history. */
const MAX_TURNS = 30;
const STORAGE_PREFIX = 'dani-reels:';
const EMPTY: ReelsConversation = Object.freeze({ study: null, turns: [] }) as ReelsConversation;

/**
 * Tab-scoped implementation over sessionStorage, with an in-memory copy so a
 * blocked or full storage never breaks the chat (it only forgets on reload).
 */
function createSessionTurnStore(): ReelsTurnStore {
    const memory = new Map<string, ReelsConversation>();
    const listeners = new Set<() => void>();

    const read = (id: string): ReelsConversation => {
        const cached = memory.get(id);
        if (cached) return cached;
        try {
            const raw = window.sessionStorage.getItem(STORAGE_PREFIX + id);
            if (raw) {
                const parsed = JSON.parse(raw) as ReelsConversation;
                const record = { study: parsed.study ?? null, turns: parsed.turns ?? [] };
                memory.set(id, record);
                return record;
            }
        } catch {
            // Storage unavailable: memory only.
        }
        return EMPTY;
    };

    const write = (id: string, record: ReelsConversation | null) => {
        if (record) memory.set(id, record);
        else memory.delete(id);
        try {
            if (record) {
                window.sessionStorage.setItem(STORAGE_PREFIX + id, JSON.stringify(record));
            } else {
                window.sessionStorage.removeItem(STORAGE_PREFIX + id);
            }
        } catch {
            // Storage unavailable or full: memory still holds it.
        }
        listeners.forEach((listener) => listener());
    };

    return {
        get: read,
        setStudy(id, study) {
            write(id, { ...read(id), study });
        },
        appendTurns(id, turns) {
            const current = read(id);
            write(id, { ...current, turns: [...current.turns, ...turns].slice(-MAX_TURNS) });
        },
        claimPending(id) {
            const pending = read(PENDING_CONVERSATION);
            if (!pending.study) return;
            write(PENDING_CONVERSATION, null);
            write(id, { ...read(id), study: pending.study });
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
}

export const reelsTurnStore: ReelsTurnStore =
    typeof window === 'undefined'
        ? {
              get: () => EMPTY,
              setStudy: () => undefined,
              appendTurns: () => undefined,
              claimPending: () => undefined,
              subscribe: () => () => undefined,
          }
        : createSessionTurnStore();

/** The video study a conversation (or the pending landing chat) is about. */
export function useVideoStudy(conversationId: string | null): VideoStudy | null {
    const id = conversationId ?? PENDING_CONVERSATION;
    return useSyncExternalStore(
        reelsTurnStore.subscribe,
        () => reelsTurnStore.get(id).study,
        () => null,
    );
}
